/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, pickBy } from 'lodash';
import { CrudListType, CrudRemoveType, CrudTemplate, ListColumns } from '../../../organisms/crud/form-types';

import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { UserDevice } from 'app/types/user-push-token';
import React from 'react';
import { DateTime } from 'app/components/atoms/date-time/date-time';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<UserDevice> => [
  {
    title: translate('common.table.columns.id'),
    dataIndex: 'id',
    key: 'id',
    render: (item: UserDevice) => {
      const id = item.id;
      if (id.length > 8) {
        return `${id.slice(0, 4)}...${id.slice(-4)}`;
      }
      return id;
    }
  },
  {
    title: translate('common.table.columns.deviceId'),
    dataIndex: 'deviceId',
    key: 'deviceId',
    render: (item: UserDevice) => item.deviceId
  },
  {
    title: translate('common.table.columns.deviceName'),
    dataIndex: 'deviceName',
    key: 'deviceName',
    size: '1fr',
    render: (item: UserDevice) => item.deviceName
  },
  {
    title: translate('common.table.columns.operatingSystem'),
    dataIndex: 'operatingSystem',
    key: 'operatingSystem',
    render: (item: UserDevice) => item.operatingSystem
  },
  {
    title: translate('common.table.columns.appVersion'),
    dataIndex: 'appVersion',
    key: 'appVersion',
    render: (item: UserDevice) => item.appVersion
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (item: UserDevice) => <DateTime date={item.createdAt} />
  },
  {
    title: translate('common.table.columns.lastSeen'),
    dataIndex: 'lastSeen',
    key: 'lastSeen',
    render: (item: UserDevice) => <DateTime date={item.lastSeen} />
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    render: (item: UserDevice) => <DateTime date={item.updatedAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  },
  {
    type: 'selectSearch',
    name: 'accessType',
    inline: true,
    label: translate('common.labels.accessType'),
    showSearch: true,
    allowClear: true,
    options: [
      {
        label: translate('common.labels.user'),
        value: 'user'
      },
      {
        label: translate('common.labels.backOffice'),
        value: 'backOffice'
      },
      {
        label: translate('common.labels.admin'),
        value: 'admin'
      }
    ]
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<UserDevice> {
  return {
    columns: listColumns(translate),
    filters: listFilters(translate),
    route: appRoutes.dashboard.users.detail.devices,
    enableExport: true,
    addButton: translate('common.actions.add'),
    canEdit: true,
    canRemove: true,
    resolveParams: (params: any, item?: UserDevice) => {
      return {
        userId: params.userId,
        deviceId: params.id ?? item?.id
      };
    },
    key: ['userDevices'],
    normalizeFilters: (data: any) => {
      const { ...filters } = data.filters;
      const result: any = {
        filters: pickBy(filters, identity),
        order: []
      };

      return result;
    },
    normalizeData: (result: any) => {
      return result;
    },
    pagination: {
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100, 500, 1000]
    },
    endpoint: apiEndpoints.userDevices,
    endpointById: apiEndpoints.userDevicesById
  };
}

/**
 * Remove.
 */

const remove = (): CrudRemoveType<UserDevice> => ({
  resolveParams: (params: any, item?: UserDevice) => {
    return {
      userId: params.userId,
      id: params.id ?? item?.id
    };
  },
  endpoint: apiEndpoints.userDevicesById
});

/**
 * Config.
 */

export function createUserDevicesTemplate(translate: TFunction): CrudTemplate<UserDevice> {
  return {
    list: list(translate),
    remove: remove()
  };
}
